import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { omit, values, isEqual, cloneDeep } from "lodash";
import { Userpilot } from "userpilot";
import { toast } from "react-toastify";

import {
  DeletedShareData,
  IComponent,
  ICreateGlobalDocumentShareData,
  ICreateShareData,
  IRole,
  IUpdatedMembers,
  IUsersContact,
  Optional
} from "store/types";
import { useActionHook, useModals } from "hooks";
import {
  useDealActions,
  useShareDialogSelector,
  IApiServiceError,
  useViewActions,
  useRolesSelector,
  useDocumentsActions
} from "store";

type IStep = "share" | "members";
export interface IUserInfo {
  last_seen: string;
  role: { id: number; name: string };
  shareId: number;
  user: IUsersContact;
  type?: string;
}

export interface ISharedComponents {
  userInfo: IUserInfo | null;
  deal_id: number[];
  components: IComponent[];
}
export interface IShareState {
  [key: string]: ISharedComponents;
}

export interface IUseShareContextHook {
  state: IShareState;
  isModalOpen: boolean;
  onClose: () => void;
  isMembersStep: boolean;
  isShareStep: boolean;
  modalTitle: string;
  switchStep: (step: IStep) => void;
  roles: IRole[];
  setStepKey: Dispatch<SetStateAction<string>>;
  handleElementClick: (id: number, type: string, holderId: number | null, state: IShareState, access: string) => void;
  deleteShare: (shareId: number, type: string) => void;
  updateUserRole: (shareId: number, roleId: number) => void;
  setFullAccess: (stepKey: string) => void;
  patchShares: () => void;
  stepKey: string;
  createShare: (data: ICreateShareData) => Promise<void>;
  createGlobalDocumentShare: (data: Optional<ICreateGlobalDocumentShareData, "document_id">) => Promise<void>;
  resendInvite: (id: number) => Promise<void>;
  shareBackHandler: () => void;
  dealId: number;
  globalDoc: boolean;
  loading: boolean;
  cb?: () => void;
}

export const useShareContextHook = (): IUseShareContextHook => {
  const {
    getSharesDataAction,
    createShareAction,
    resendInviteAction,
    updateSharesAction,
    getDashboardSharesAction,
    getEmptyShareTreeAction
  } = useDealActions();
  const { createGlobalDocumentShareAction, getGlobalDocumentShareAction, updateGlobalDocumentShareAction } =
    useDocumentsActions();
  const { data: shares, action: getShares } = useActionHook(getSharesDataAction);
  const { data: globalDocShares, action: getGlobalDocShares } = useActionHook(getGlobalDocumentShareAction);
  const { action: getDashboardShares } = useActionHook(getDashboardSharesAction);
  const { data: emptyTree, action: getEmptyShareTree } = useActionHook(getEmptyShareTreeAction);

  // const deal_id = useDealIdSelector();

  const roles: IRole[] = useRolesSelector();
  const { errorModal } = useModals();
  const { setShareDialog } = useViewActions();
  //can be "share" if we on "share" step or "share.id" if we select some user
  //"stepKey" is used to update state when we select some user, or update main  "share" state, if we on first step
  const [step, setStep] = useState<IStep>("share");
  const [stepKey, setStepKey] = useState<string>("share");
  const [state, setState] = useState<IShareState>({});

  const isShareStep: boolean = step === "share";
  const isMembersStep: boolean = step === "members";
  const modalTitle = isShareStep ? "Share Workspace" : "Workspace Members";
  const [deletedShareIds, setDeletedShareIds] = useState<DeletedShareData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const switchStep = (step: IStep) => {
    setStep(step);
  };

  const shareDialog = useShareDialogSelector();
  const { elements, type, dealId, onBeforeClose, holderId = null, globalDoc = false, cb } = shareDialog || {};

  const getSharesData = useCallback(async () => {
    if (!dealId) return;
    await getShares(dealId);
  }, [dealId]);

  const getGlobalDocSharesData = useCallback(async () => {
    if (!elements?.length) return;
    await getGlobalDocShares(elements[0]);
  }, [elements]);

  const getEmptySharesData = useCallback(async () => {
    if (!dealId) return;
    await getEmptyShareTree(dealId);
  }, [dealId]);

  const { action: updateShares } = useActionHook(updateSharesAction, {
    onSuccess: () => {
      getSharesData();
      toast("Members updated");
      setDeletedShareIds([]);
    }
  });

  const { action: updateGlobalDocShares } = useActionHook(updateGlobalDocumentShareAction, {
    onSuccess: () => {
      getGlobalDocSharesData();
      toast("Members updated");
      setDeletedShareIds([]);
    }
  });

  const { action: createShares } = useActionHook(createShareAction, {
    onSuccess: () => {
      getSharesData();
      dealId && getDashboardShares(dealId, false);
      setLoading(false);
    },
    onReject: (error) => {
      setLoading(false);
      if ((error as IApiServiceError).status === 429) {
        errorModal({
          msg: "We detected suspicious behavior. please try again in a couple of minutes."
        });
      }
      errorModal({
        msg: ((error as IApiServiceError).errors as unknown as string[]).join("\n")
      });
    }
  });

  const { action: createGlobalDocumentShares } = useActionHook(createGlobalDocumentShareAction, {
    onSuccess: () => {
      getGlobalDocSharesData();
      setLoading(false);
    },
    onReject: (error) => {
      setLoading(false);
      if ((error as IApiServiceError).status === 429) {
        errorModal({
          msg: "We detected suspicious behavior. please try again in a couple of minutes."
        });
      }
      errorModal({
        msg: ((error as IApiServiceError).errors as unknown as string[]).join("\n")
      });
    }
  });

  const updateUserRole = (shareId: number, roleId: number) => {
    const newRoleName = roles.find((role) => role.id === roleId);

    setState((prev) => {
      return {
        ...prev,
        [shareId]: { ...prev[shareId], userInfo: { ...prev[shareId].userInfo, role: newRoleName } }
      };
    });
  };

  const onClose = useCallback(() => {
    onBeforeClose?.();
    setShareDialog(null);
    setStep("share");
    setStepKey("share");
    setState({}); //clear three state
  }, [onBeforeClose]);

  // ---------------------- Post Invite -------------------
  const createShare = useCallback(
    async (data: ICreateShareData) => {
      setLoading(true);
      try {
        const { roleId, emails, message, share } = data;
        if (!dealId) return;
        await createShares({
          emails,
          message,
          roleId,
          dealId: dealId,
          share: share
        });
        if (share.deal_id.length) {
          Userpilot.track("Share Deal Externally");
        }
      } catch (error) {
        console.log(error);
      }
    },
    [dealId]
  );

  const createGlobalDocumentShare = useCallback(
    async (data: Optional<ICreateGlobalDocumentShareData, "document_id">) => {
      setLoading(true);
      try {
        const { role_id, emails, message } = data;
        if (!elements?.length) return;

        await createGlobalDocumentShares({
          emails,
          message,
          role_id,
          document_id: elements
        });
        Userpilot.track("Share Global Document");
      } catch (error) {
        console.log(error);
      }
    },
    [elements]
  );

  // ------------------ Patch Share Dialog User ------------------------------
  const patchShares = useCallback(async (): Promise<void> => {
    const membersArray = values(omit(state, "share"));

    const getUpdatedMembers = () => {
      return membersArray
        .map((item) => {
          const oldShareValue = shares?.find((share) => share.id === item?.userInfo?.shareId);
          const hasChanges =
            !isEqual(item?.components, oldShareValue?.components) ||
            !isEqual(item.deal_id, oldShareValue?.deal_id) ||
            !isEqual(item?.userInfo?.role, oldShareValue?.role);

          if (!hasChanges) return;

          return {
            id: item?.userInfo?.shareId,
            type: item.userInfo?.type,
            role_id: item?.userInfo?.role.id,
            components: !item?.deal_id?.length ? item.components : [],
            deal_id: item?.deal_id || []
          };
        })
        .filter((item) => item !== undefined) as IUpdatedMembers[];
    };

    const updateGlobalDocumentShares = async () => {
      if (!elements?.length) return;
      await updateGlobalDocShares({
        document_id: elements[0],
        updated: getUpdatedMembers() || [],
        deleted: deletedShareIds
      });
    };

    const updateDealShares = async () => {
      if (!dealId) return;
      await updateShares({
        dealId,
        updated: getUpdatedMembers() || [],
        deleted: deletedShareIds
      });
      getDashboardShares(dealId, false);
    };

    try {
      if (globalDoc) {
        await updateGlobalDocumentShares();
      } else {
        await updateDealShares();
      }
    } catch (error) {
      console.log(error);
    }
  }, [dealId, state, elements, globalDoc, shares, deletedShareIds]);

  // ---------------------------------- Resend Invite -----------------------------

  const resendInvite = async (id: number) => {
    try {
      resendInviteAction(id);
    } catch (error) {
      if ((error as IApiServiceError).status === 429) {
        errorModal({
          msg: "We detected suspicious behavior. please try again in a couple of minutes."
        });
      }
      console.error(error);
    }
  };

  // ---------------------------------- utils -----------------------------

  const setFullAccess = (stepKey: string) => {
    if (!dealId) return;
    setState((prev) => {
      if (prev[stepKey]?.deal_id?.includes(dealId)) {
        return {
          ...prev,
          [stepKey]: {
            ...prev[stepKey],
            deal_id: []
          }
        };
      }
      return {
        ...prev,
        [stepKey]: {
          ...prev[stepKey],
          deal_id: [dealId]
        }
      };
    });
  };

  const deleteShare = (shareId: number, type: string) => {
    setStepKey("share");
    setDeletedShareIds((prev) => {
      return [...prev, { id: shareId, type }];
    });
    setState((prev) => {
      const newState = { ...prev };
      delete newState[shareId];
      return newState;
    });
  };

  const shareBackHandler = () => {
    switchStep("share");
    setStepKey("share");
    //reset users state
    writeSharesToState();
    setDeletedShareIds([]);
  };

  const writeSharesToState = () => {
    const sharesToState = shares || globalDocShares;
    sharesToState?.forEach((share) => {
      setState((prev) => {
        return {
          ...prev,
          [share.id]: {
            components: share.components,
            deal_id: share.deal_id,
            userInfo: {
              user: share.user,
              shareId: share.id,
              role: share.role,
              last_seen: share.last_seen,
              type: share.type
            }
          }
        };
      });
    });
  };

  const accessHandler = (type: string, access: string) => {
    if (type === "section") {
      if (access === "none") {
        return "full";
      } else if (access === "full") {
        return "none";
      } else if (access === "partial") {
        return "full";
      }
    } else {
      if (access === "full") {
        return "none";
      } else if (access === "partial") {
        return "full";
      } else if (access === "none") {
        return "full";
      }
    }
  };

  const updateChildrenAccess = (id: number, type: string, currentState: IComponent[], access: string): IComponent[] => {
    return currentState.map((item: IComponent) => {
      if (item.id === id && item.type === type) {
        // Update the access property for the matching element
        if (type !== "section" && access === "full") {
          return {
            ...item,
            access: "none",
            children: updateAccessForAllChildren(item.children, item.access as string)
          };
        } else {
          return {
            ...item,
            access: accessHandler(type, access),
            children: updateAccessForAllChildren(item.children, item.access as string)
          };
        }
      } else if (item.children.length && item.type === "section") {
        // Recursively update the access property in the children

        return { ...item, children: updateChildrenAccess(id, type, item.children, access) };
      }
      return item;
    });
  };

  const updateAccessForAllChildren = (children: IComponent[], access: string): IComponent[] => {
    return children.map((child: IComponent) => {
      // Update access property for all children to 'none'
      return {
        ...child,
        access: child.isPrivate ? "none" : accessHandler(child.type, access),
        children: updateAccessForAllChildren(child.children, access)
      };
    });
  };

  const updateAccessHandler = (
    holderId: number | null,
    currentState: IComponent[],
    id: number,
    type: string,
    access: string
  ) => {
    let stateClone = cloneDeep(currentState);
    // resetAccess if all children are none change the parent to none too
    const resetAccess = (holderId: number | null, tree: IComponent[]) => {
      if (!holderId) return;
      for (const item of tree) {
        if (item.id === holderId) {
          const isChildrenNone = item.children.filter((item: IComponent) => item.access !== "none");
          if (!isChildrenNone.length) {
            item.access = "none";
            resetAccess(item.holderId, stateClone);
          }
          return;
        }
        if (item.children.length) {
          resetAccess(holderId, item.children);
        }
      }
    };
    const selectPartialAccessForParents = (holderId: number | null, tree: IComponent[]) => {
      if (!holderId) return;
      for (const item of tree) {
        if (item.isPrivate) return;
        if (item.id === holderId && item.type === "section") {
          item.access = "partial";
          selectPartialAccessForParents(item.holderId, stateClone);
          return;
        }
        if (item.children.length) {
          selectPartialAccessForParents(holderId, item.children);
        }
      }
    };
    // update all parent section access to partial if select to any children
    selectPartialAccessForParents(holderId, stateClone);
    // update the access property for childrens
    stateClone = updateChildrenAccess(id, type, stateClone, access);

    // reset the access property for parents if all children are none
    resetAccess(holderId, stateClone);
    return stateClone;
  };

  const handleElementClick = (
    id: number,
    type: string,
    holderId: number | null,
    state: IShareState,
    access: string
  ) => {
    setState({
      ...state,
      [stepKey]: {
        deal_id: state[stepKey].deal_id,
        components: updateAccessHandler(holderId, state[stepKey].components, id, type, access),
        userInfo: state[stepKey].userInfo
      }
    });
  };

  // ---------------------- useEffects ----------------------

  useEffect(() => {
    if (!dealId) return;
    getSharesData();
    getEmptySharesData();
  }, [dealId]);

  useEffect(() => {
    if (!globalDoc) return;
    getGlobalDocSharesData();
  }, [globalDoc]);

  useEffect(() => {
    if (!emptyTree) return;
    setState((prev) => {
      const id = elements?.[0];
      const typeForHandler = type && type !== "deal_id" ? type.replace("_id", "") : null;

      return {
        ...prev,
        share: {
          components:
            id && typeForHandler ? updateAccessHandler(holderId, emptyTree, id, typeForHandler, "none") : emptyTree,
          userInfo: null,
          deal_id: dealId && elements?.length && type === "deal_id" ? [dealId] : []
        }
      };
    });
  }, [emptyTree]);

  useEffect(() => {
    if (!shares && !globalDocShares) return;
    writeSharesToState();
  }, [shares, globalDocShares]);

  return {
    shareBackHandler,
    state,
    deleteShare,
    handleElementClick,
    updateUserRole,
    isModalOpen: !!shareDialog,
    createShare,
    createGlobalDocumentShare,
    setFullAccess,
    onClose,
    isMembersStep,
    isShareStep,
    modalTitle,
    switchStep,
    roles,
    patchShares,
    setStepKey,
    stepKey,
    resendInvite,
    dealId: dealId || 0,
    globalDoc,
    loading,
    cb
  };
};
